'use client';

import { useState } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { ArrowLeftIcon } from '@depop/web-ui-kit/Icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@depop/web-ui-kit/Icons/ArrowRightIcon';
import { clsx } from 'clsx';

import TestimonialItem, { type Testimonial } from './TestimonialItem';
import {
  ITEMS_PER_SLIDE,
  TESTIMONIAL_MIN_WIDTH,
  TESTIMONIAL_GAP,
} from './constants';
import styles from './styles.module.css';

import { CampaignSegment } from '@/app/[locale]/campaign/components/CampaignSegment/CampaignSegment';
import {
  Carousel,
  type ItemsPerSlide,
  type NavigationState,
} from '@/components/Carousel';
import { Container, ContainerWidth } from '@/components/Container';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';

type TitleSize = 'lg' | 'xl';

type TestimonialsProps = {
  testimonials: Testimonial[];
  title: string;
  description?: string;
  titleSize?: TitleSize;
  itemsPerSlide?: ItemsPerSlide;
  className?: string;
};

export function Testimonials({
  testimonials,
  title,
  description,
  titleSize = 'xl',
  itemsPerSlide = ITEMS_PER_SLIDE,
  className,
}: TestimonialsProps) {
  const [navigationState, setNavigationState] =
    useState<NavigationState | null>(null);

  const testimonialItems = testimonials.map((testimonial) => (
    <TestimonialItem key={testimonial.author} testimonial={testimonial} />
  ));

  return (
    <Container width={ContainerWidth.Default} className={className}>
      <section aria-labelledby="testimonials-heading">
        <div className={styles.headerRow}>
          <div>
            <CampaignSegment.Title
              id="testimonials-heading"
              variant={titleSize}
            >
              {title}
            </CampaignSegment.Title>

            {description && (
              <Body className={styles.description} variant="md-regular">
                {description}
              </Body>
            )}
          </div>

          <div
            className={styles.arrowsWrap}
            role="group"
            aria-label="Testimonials navigation"
          >
            {(navigationState?.canGoLeft || navigationState?.canGoRight) && (
              <>
                <button
                  type="button"
                  aria-label="View previous testimonials"
                  onClick={() => navigationState.goLeft()}
                  disabled={!navigationState.canGoLeft}
                  className={clsx(
                    styles.arrowBtn,
                    !navigationState.canGoLeft && styles.arrowBtnDisabled
                  )}
                >
                  <ArrowLeftIcon size={16} />
                </button>
                <button
                  type="button"
                  aria-label="View next testimonials"
                  onClick={() => navigationState.goRight()}
                  disabled={!navigationState.canGoRight}
                  className={clsx(
                    styles.arrowBtn,
                    !navigationState.canGoRight && styles.arrowBtnDisabled
                  )}
                >
                  <ArrowRightIcon size={16} />
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.scrollableRowContainer}>
          <ScrollableItemRow
            itemsPerRow={testimonials.length}
            minItemWidth={TESTIMONIAL_MIN_WIDTH}
            itemGap={8}
            aria-label="Customer testimonials carousel"
          >
            {testimonialItems}
          </ScrollableItemRow>
        </div>
        <div className={styles.carouselContainer}>
          <Carousel
            id="testimonials__carousel"
            items={testimonialItems}
            itemsPerSlide={itemsPerSlide}
            itemGap={TESTIMONIAL_GAP}
            ariaLabel="Customer testimonials carousel"
            onNavigationStateChange={setNavigationState}
          />
        </div>
      </section>
    </Container>
  );
}
